import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useMediaQuery } from "@uidotdev/usehooks";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CirclePlus, Copy, Plus, Send, Rocket } from "lucide-react";
import { AxiosError } from "axios";
import {
  format,
  parseISO,
  isValid,
  addHours,
  isBefore,
  startOfDay,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";
import {
  formatCompactWeekRange,
  formatScheduleRangeParts,
} from "@/utils/format-date";
import { isSlotInPast } from "@/utils/time-helpers";
import { z } from "zod";
import {
  WeeklyCalendar,
  type WeeklyCalendarHandle,
} from "@/components/weekly-calendar";
import { CalendarFilterDropdown } from "@/components/weekly-calendar/calendar-filter-dropdown";
import { PageContainer } from "@/components/ui/page-container";
import { GymScheduleStatusPill } from "@/components/gym-schedule-status-pill";
import { getPillColor } from "@/components/ui/pill/pill-config";
import { Button } from "@/components/ui/button";
import {
  SCHEDULE_STATUS,
  STATUS_COLORS,
  getScheduleOutlineColor,
} from "@/utils/enum/gym-schedules";
import { PROTECTED_ROUTES } from "@/utils/navigation";
import { toast } from "@/components/ui/toast";
import { useGeneralCalendarFilters } from "@/hooks/calendar/use-general-calendar-filters";
import { useCalendarFilterStore } from "@/stores/calendar-filters-store";
import { useDisplayedScheduleWeek } from "@/hooks/calendar/use-displayed-schedule-week";
import { useGymStore } from "@/stores/gym-store";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { useEvents } from "@/queries/use-events";
import { EVENTABLE_TYPE, GYM_SCHEDULE_VIEW_TYPES } from "@/constants/events";
import { QuickAddEventPanel } from "@/components/quick-add-event-panel";
import { DuplicateScheduleModal } from "@/components/duplicate-schedule-modal";
import { CreateScheduleModal } from "@/components/create-schedule-modal";
import { ConfirmDialog } from "@/components/ui/dialog/confirm-dialog";
import { PublishScheduleWeeksModal } from "@/components/publish-schedule-weeks-modal";
import {
  useSubmitGymSchedule,
  usePublishGymSchedule,
} from "@/queries/use-gym-schedules";
import { getApiErrorMessages } from "@/utils/api-error";
import type { EventableType } from "@/api/events/types";
import type { GymScheduleStatus } from "@/api/gym-schedules/types";
import { breakpoints } from "@/styles/theme";
import { useGymScheduleStore } from "@/stores/gym-schedule-store";
import { getScheduleDateConstraints } from "@/utils/gym-schedule-dates";

export const Route = createFileRoute("/(protected)/allocation/")({
  validateSearch: z.object({
    week: z.string().optional(),
  }),
  component: RouteComponent,
  loader: () => ({
    hasActionButtons: true,
  }),
});

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const navigate = Route.useNavigate();
  const { setGymSchedule } = useGymScheduleStore();
  const { week } = Route.useSearch();
  const calendarRef = useRef<WeeklyCalendarHandle>(null);
  const prevDisplayedWeek = useRef<
    { id: number; status: GymScheduleStatus } | null | undefined
  >(undefined);
  const isMobile = !useMediaQuery(`(min-width: ${breakpoints.md})`);

  const initialDate = useMemo(() => {
    if (!week) return new Date();
    const parsed = parseISO(week);
    return isValid(parsed) ? parsed : new Date();
  }, [week]);

  const [dateRange, setDateRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);

  const [quickAddDate, setQuickAddDate] = useState<Date | null>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [anchorClickY, setAnchorClickY] = useState<number | null>(null);
  const [anchorClickX, setAnchorClickX] = useState<number | null>(null);
  const [pendingDates, setPendingDates] = useState<{
    startsAt: Date;
    endsAt: Date;
    recurrenceFrequency?: string;
    recurrenceUntil?: string;
  } | null>(null);

  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isCreateScheduleModalOpen, setIsCreateScheduleModalOpen] =
    useState(false);
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false);
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);

  const { mutate: submitSchedule, isPending: isSubmitting } =
    useSubmitGymSchedule();
  const { mutate: publishSchedule, isPending: isPublishing } =
    usePublishGymSchedule();

  const { filters } = useCalendarFilterStore();

  const startsAtMin = dateRange ? format(dateRange.start, "yyyy-MM-dd") : "";
  const startsAtMax = dateRange ? format(dateRange.end, "yyyy-MM-dd") : "";

  const gymId = filters.gymIds[0];

  const { selectedGym, availableGyms } = useGymStore();
  const scheduleGym =
    availableGyms.find((gym) => gym.id === gymId) ?? selectedGym;

  const isScheduleRelevant =
    filters.gymIds.length === 1 &&
    filters.viewType !== null &&
    (GYM_SCHEDULE_VIEW_TYPES as readonly string[]).includes(filters.viewType);

  const {
    gymSchedule,
    query: schedulesQuery,
    displayedWeek,
    weekStatus,
    isUnappliedDraft,
    effectiveSchedule,
    effectiveVisualStatus,
  } = useDisplayedScheduleWeek({
    gymId: gymId ?? 0,
    timezone: scheduleGym?.timezone,
    weekStart: dateRange?.start,
    weekEnd: dateRange?.end,
    enabled: isScheduleRelevant && !!gymId && !!dateRange,
  });

  const gymScheduleStatus = effectiveSchedule?.status ?? null;

  const {
    filterGroups,
    activeFilterCount,
    onGroupChange,
    onClearAll,
    canClearAll,
    eventParams,
  } = useGeneralCalendarFilters(weekStatus);

  const eventsQuery = useEvents(
    { ...eventParams, startsAtMin, startsAtMax },
    { enabled: !!dateRange && eventParams.gymIds.length > 0 },
  );
  const { refetch: refetchEvents } = eventsQuery;

  const events = eventsQuery.data?.events ?? [];

  const hasNarrowingFilters =
    filters.disciplineIds.length > 0 ||
    filters.staffIds.length > 0 ||
    filters.roomIds.length > 0;

  // keepPreviousData rebuilds a fresh gymSchedule object on every week's
  // resolve, so deriving the toast effect's dep straight from gymSchedule
  // would re-fire it on every navigated week within the same schedule.
  const hasNoSchedule = gymSchedule === null;

  const canQuickAdd =
    isScheduleRelevant &&
    effectiveSchedule != null &&
    effectiveSchedule.status !== "completed";

  const scheduleStartsAt = effectiveSchedule?.startsAt;
  const scheduleEndsAt = effectiveSchedule?.endsAt;
  const quickAddDateConstraints = useMemo(
    () =>
      scheduleStartsAt && scheduleEndsAt
        ? getScheduleDateConstraints(
            scheduleStartsAt,
            scheduleEndsAt,
            scheduleGym?.timezone,
          )
        : undefined,
    [scheduleStartsAt, scheduleEndsAt, scheduleGym?.timezone],
  );

  const isViewedWeekPast = dateRange
    ? isBefore(startOfDay(dateRange.end), startOfDay(new Date()))
    : false;

  const validEventType: EventableType =
    filters.viewType === EVENTABLE_TYPE.GroupSession ||
    filters.viewType === EVENTABLE_TYPE.FloorDuty
      ? filters.viewType
      : EVENTABLE_TYPE.GroupSession;

  // Close the panel when quick-add conditions are no longer met
  useEffect(() => {
    if (!canQuickAdd) setQuickAddDate(null);
  }, [canQuickAdd]);

  // Close any open panel and clear anchor on viewport size change
  useEffect(() => {
    setQuickAddDate(null);
    setPendingDates(null);
    setAnchorElement(null);
    setAnchorClickY(null);
    setAnchorClickX(null);
  }, [isMobile]);

  useEffect(() => {
    if (eventsQuery.error) {
      const axiosError = eventsQuery.error as AxiosError<{ errors: string[] }>;
      const errorMessage =
        axiosError.response?.data?.errors?.join(", ") ||
        eventsQuery.error.message;
      toast.error(t("pages:calendar.view.toaster.error"), {
        description: errorMessage,
      });
    }
  }, [eventsQuery.error, t]);

  useEffect(() => {
    if (
      !eventsQuery.isSuccess ||
      !(schedulesQuery.isSuccess || schedulesQuery.isError) ||
      !isScheduleRelevant ||
      hasNarrowingFilters
    )
      return;

    if (isUnappliedDraft) {
      toast.information(
        t("pages:calendar.view.toaster.unappliedBlueprint.title"),
        {
          description: t(
            "pages:calendar.view.toaster.unappliedBlueprint.description",
          ),
        },
      );
      return;
    }

    if (hasNoSchedule) {
      toast.information(t("pages:calendar.view.toaster.noSchedule.title"), {
        description: t("pages:calendar.view.toaster.noSchedule.description"),
      });
    }
  }, [
    eventsQuery.isSuccess,
    schedulesQuery.isSuccess,
    schedulesQuery.isError,
    isScheduleRelevant,
    hasNoSchedule,
    isUnappliedDraft,
    hasNarrowingFilters,
    t,
  ]);

  useEffect(() => {
    const prev = prevDisplayedWeek.current;
    const current = displayedWeek
      ? { id: displayedWeek.id, status: displayedWeek.status }
      : null;
    prevDisplayedWeek.current = current;

    if (
      displayedWeek &&
      prev &&
      current &&
      prev.id === current.id &&
      prev.status !== "approved" &&
      current.status === "approved"
    ) {
      toast.success(t("pages:calendar.view.toaster.approved.title"), {
        description: t("pages:calendar.view.toaster.approved.description", {
          range: formatCompactWeekRange(
            toZonedTime(
              parseISO(displayedWeek.startsAt),
              scheduleGym?.timezone ?? "UTC",
            ),
            toZonedTime(
              parseISO(displayedWeek.endsAt),
              scheduleGym?.timezone ?? "UTC",
            ),
            i18n.language,
          ),
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedWeek?.id, displayedWeek?.status]);

  const handleSubmitConfirm = useCallback(() => {
    if (!gymId || !gymSchedule) return;
    submitSchedule(
      { gymId, scheduleId: gymSchedule.id },
      {
        onSuccess: () => {
          toast.success(t("pages:calendar.view.toaster.submit.success.title"), {
            description: t(
              "pages:calendar.view.toaster.submit.success.description",
            ),
          });
          refetchEvents();
        },
        onError: (error) => {
          toast.error(t("pages:calendar.view.toaster.submit.error.title"), {
            description: `${t("pages:calendar.view.toaster.submit.error.description")}: ${getApiErrorMessages(error)}`,
          });
        },
      },
    );
  }, [gymId, gymSchedule, submitSchedule, refetchEvents, t]);

  const handlePublishConfirm = useCallback(
    (weekIndexes: number[]) => {
      if (!gymId || !gymSchedule) return;
      publishSchedule(
        { gymId, scheduleId: gymSchedule.id, weekIndexes },
        {
          onSuccess: () => {
            setIsPublishConfirmOpen(false);
            toast.success(
              t("pages:calendar.view.toaster.publish.success.title"),
              {
                description: t(
                  "pages:calendar.view.toaster.publish.success.description",
                ),
              },
            );
            refetchEvents();
          },
          onError: (error) => {
            toast.error(t("pages:calendar.view.toaster.publish.error.title"), {
              description: `${t("pages:calendar.view.toaster.publish.error.description")}: ${getApiErrorMessages(error)}`,
            });
          },
        },
      );
    },
    [gymId, gymSchedule, publishSchedule, refetchEvents, t],
  );

  const handleDateRangeChange = useCallback(
    (start: Date, end: Date) => {
      setDateRange({ start, end });
      navigate({
        search: { week: format(start, "yyyy-MM-dd") },
        replace: true,
      });
    },
    [navigate],
  );

  const handleDayClick = useCallback(
    (
      date: Date,
      element?: HTMLElement,
      clickY?: number,
      clickX?: number,
      hasPreciseTime?: boolean,
    ) => {
      if (!canQuickAdd) return;
      if (isSlotInPast(date, hasPreciseTime)) return;
      setPendingDates(null);
      setQuickAddDate(date);
      setAnchorElement(element ?? null);
      setAnchorClickY(clickY ?? null);
      setAnchorClickX(clickX ?? null);
    },
    [canQuickAdd],
  );

  const clearAnchor = useCallback(() => {
    setAnchorElement(null);
    setAnchorClickY(null);
    setAnchorClickX(null);
  }, []);

  const handleQuickAddClose = useCallback(() => {
    setQuickAddDate(null);
    setPendingDates(null);
    clearAnchor();
  }, [clearAnchor]);

  const handleQuickAddSuccess = useCallback(() => {
    setQuickAddDate(null);
    setPendingDates(null);
    clearAnchor();
    refetchEvents();
  }, [clearAnchor, refetchEvents]);

  const handleDatesChange = useCallback(
    (
      startsAt: Date,
      endsAt: Date,
      recurrenceFrequency?: string,
      recurrenceUntil?: string,
    ) => {
      setPendingDates({
        startsAt,
        endsAt,
        recurrenceFrequency,
        recurrenceUntil,
      });
      setAnchorElement((prev) => {
        if (!prev) return prev;
        return calendarRef.current?.getColumnForDate(startsAt) ?? prev;
      });
    },
    [],
  );

  const pendingEvent = useMemo(() => {
    if (!quickAddDate) return undefined;
    return (
      pendingDates ?? {
        startsAt: quickAddDate,
        endsAt: addHours(quickAddDate, 1),
      }
    );
  }, [quickAddDate, pendingDates]);

  const filterContent = (
    <CalendarFilterDropdown
      groups={filterGroups}
      activeCount={activeFilterCount}
      onGroupChange={onGroupChange}
      onClearAll={onClearAll}
      canClearAll={canClearAll}
    />
  );

  const scheduleStatusColor = effectiveVisualStatus
    ? STATUS_COLORS[effectiveVisualStatus]
    : undefined;
  const schedulePillColors = scheduleStatusColor
    ? getPillColor(scheduleStatusColor)
    : null;

  const statusElement = effectiveVisualStatus ? (
    <GymScheduleStatusPill
      status={effectiveVisualStatus}
      className="flex w-full md:inline-flex md:w-auto"
    />
  ) : null;

  const borderColor = getScheduleOutlineColor(
    effectiveVisualStatus ?? undefined,
    schedulePillColors,
  );

  const containerBorderStyle = {
    borderTop: `4px solid ${borderColor}`,
    borderLeft: `4px solid ${borderColor}`,
    borderRight: `4px solid ${borderColor}`,
  };

  const actionButtons = (btnClass?: string) => {
    const duplicateBtn = effectiveSchedule ? (
      <Button
        variant="secondary"
        leftIcon={<Copy />}
        className={btnClass}
        onClick={() => setIsDuplicateModalOpen(true)}
      >
        {t("common:actions.duplicate")}
      </Button>
    ) : null;

    if (gymScheduleStatus === SCHEDULE_STATUS.draft) {
      return (
        <>
          {duplicateBtn}
          <Button
            variant="primary"
            leftIcon={<Send />}
            className={btnClass}
            onClick={() => setIsSubmitConfirmOpen(true)}
            loading={isSubmitting}
          >
            {t("pages:calendar.actions.send")}
          </Button>
        </>
      );
    }

    if (
      gymScheduleStatus === SCHEDULE_STATUS.pending ||
      gymScheduleStatus === SCHEDULE_STATUS.approved
    ) {
      return (
        <>
          {duplicateBtn}
          <Button
            variant="primary"
            leftIcon={<Rocket />}
            className={btnClass}
            onClick={() => setIsPublishConfirmOpen(true)}
            loading={isPublishing}
          >
            {t("pages:calendar.actions.publish")}
          </Button>
        </>
      );
    }

    return (
      <>
        {duplicateBtn}
        <Button
          variant="primary"
          leftIcon={<CirclePlus />}
          className={btnClass}
          onClick={() => setIsCreateScheduleModalOpen(true)}
        >
          {t("pages:calendar.actions.createSchedule")}
        </Button>
      </>
    );
  };

  return (
    <>
      <div
        className="absolute top-6 right-6 z-10 hidden md:flex md:items-center md:gap-2"
        data-testid="schedule-actions-desktop"
      >
        {actionButtons()}
      </div>

      <div className="flex h-full flex-col">
        <MobilePageTitle className="mx-6 mt-5">
          {t("pages:calendar.title")}
        </MobilePageTitle>

        <div
          className="mx-6 mt-2.5 flex gap-2 md:hidden"
          data-testid="schedule-actions-mobile"
        >
          {actionButtons("w-full")}
        </div>

        <PageContainer
          className="mx-6 mb-0 h-full flex-1 rounded-b-none px-2 py-2.5 md:mb-0 lg:mb-0"
          style={containerBorderStyle}
        >
          <WeeklyCalendar
            ref={calendarRef}
            initialDate={initialDate}
            events={events}
            isLoading={eventsQuery.isLoading}
            filterContent={filterContent}
            statusElement={statusElement}
            onDateRangeChange={handleDateRangeChange}
            onDayClick={canQuickAdd ? handleDayClick : undefined}
            pendingEvent={pendingEvent}
          />
          {quickAddDate && gymId && (
            <QuickAddEventPanel
              key={quickAddDate.toISOString()}
              eventType={validEventType}
              clickedDate={quickAddDate}
              gymId={gymId}
              gymScheduleId={effectiveSchedule?.id ?? null}
              dateConstraints={quickAddDateConstraints}
              onClose={handleQuickAddClose}
              onSuccess={handleQuickAddSuccess}
              onDatesChange={handleDatesChange}
              anchorElement={anchorElement}
              anchorClickY={anchorClickY ?? undefined}
              anchorClickX={anchorClickX ?? undefined}
            />
          )}
        </PageContainer>
      </div>

      {canQuickAdd && effectiveSchedule && !isViewedWeekPast && (
        <Button
          variant="primary"
          data-testid="schedule-add-event"
          className="fixed right-10 bottom-[66px] size-14 rounded-full shadow-2xl md:right-14 md:bottom-[38px]"
          onClick={() => {
            setGymSchedule(
              effectiveSchedule.id,
              effectiveSchedule.status,
              effectiveSchedule.startsAt,
              effectiveSchedule.endsAt,
            );
            navigate({ to: PROTECTED_ROUTES.CALENDAR_NEW_EVENT });
          }}
        >
          <Plus className="size-6" strokeWidth={3} />
        </Button>
      )}

      <DuplicateScheduleModal
        open={isDuplicateModalOpen}
        blueprintId={gymSchedule?.gymScheduleBlueprintId ?? null}
        onClose={() => setIsDuplicateModalOpen(false)}
      />

      <CreateScheduleModal
        open={isCreateScheduleModalOpen}
        onClose={() => setIsCreateScheduleModalOpen(false)}
      />

      <ConfirmDialog
        isOpen={isSubmitConfirmOpen}
        onOpenChange={setIsSubmitConfirmOpen}
        onConfirm={handleSubmitConfirm}
        title={t("pages:calendar.confirmSend.title")}
        description={t("pages:calendar.confirmSend.description", {
          ...(gymSchedule
            ? formatScheduleRangeParts(
                toZonedTime(
                  parseISO(gymSchedule.startsAt),
                  scheduleGym?.timezone ?? "UTC",
                ),
                toZonedTime(
                  parseISO(gymSchedule.endsAt),
                  scheduleGym?.timezone ?? "UTC",
                ),
                i18n.language,
              )
            : { startDate: "", endDate: "" }),
        })}
        confirmText={t("pages:calendar.confirmSend.confirm")}
        titleClassName="md:max-w-none"
        confirmLeftIcon={<Send />}
      />

      <PublishScheduleWeeksModal
        open={isPublishConfirmOpen}
        weeks={gymSchedule?.weeks ?? []}
        timezone={scheduleGym?.timezone}
        isPublishing={isPublishing}
        onClose={() => setIsPublishConfirmOpen(false)}
        onConfirm={handlePublishConfirm}
      />
    </>
  );
}
